import { useMemo } from "react";

import { DemandHqId } from "@buildresonance/resonance-lib-entities";
import { DEMAND_API_DOMAIN_NAME } from "@lib/common/domainNames";
import axios, { AxiosError, AxiosResponse } from "axios";
import { useMutation } from "react-query";
import { useDebouncedCallback } from "use-debounce";

const PRODUCT_AUTOCOMPLETE_DEBOUNCE_DELAY_IN_MS = 150;

export interface UseProductAutoCompleteReturn {
    /** Hit this as the user types, will automatically debounce */
    debouncedQuery: (queryParam: string) => void;
    productAutoCompletes: string[] | undefined;
}

const useProductAutoComplete = (
    demandHqId: DemandHqId | undefined,
): UseProductAutoCompleteReturn => {
    const { mutate, data } = useMutation<
        AxiosResponse<{ productAutoCompletes: string[] }>,
        AxiosError,
        { query: string }
    >(({ query }) =>
        axios.get(`/demand-hqs/${demandHqId}/product-auto-completes`, {
            baseURL: `https://${DEMAND_API_DOMAIN_NAME}`,
            params: { query, first: 10 },
        }),
    );

    const debouncedQuery = useDebouncedCallback(
        (queryParam) => mutate({ query: queryParam }),
        PRODUCT_AUTOCOMPLETE_DEBOUNCE_DELAY_IN_MS,
    );

    const productAutoCompletes = useMemo(
        () => data?.data?.productAutoCompletes,
        [data],
    );

    return { debouncedQuery, productAutoCompletes };
};

export { useProductAutoComplete };
